import React from 'react';
import { useLingui } from '@lingui/react/macro';
import upperFirst from 'lodash-es/upperFirst';

import {
  DEFAULT_DOG_SIZES,
  DEFAULT_WEIGHT_LABEL,
} from '@rover/constants/src/translations/sitterProfilePage.constants';
// eslint-disable-next-line rover/no-namespace-import-icons
import * as icons from '@rover/icons';
import type { SvgIcon } from '@rover/types/src/SvgIcon';
import { Box, Flex, Text } from '@rover/kibble/core';
import IconBlock from '@rover/kibble/patterns/IconBlock/IconBlock';
import { Attribute } from '@rover/rsdk/src/apiClient/latest/model/attribute';
import { CountryConfigurationDogSizesItem } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationDogSizesItem';
import { camelizeString } from '@rover/rsdk/src/modules/Network/object-utilities';

type PetAttribute = Attribute & {
  value: string;
};

type Props = {
  petsAttributes: PetAttribute[];
  dogSizes?: CountryConfigurationDogSizesItem[];
  weightLabel?: string;
  cat: boolean;
  small?: boolean;
  medium?: boolean;
  large?: boolean;
  giant?: boolean;
};

const isCat = (pa): boolean => pa.key === 'cat';

const PetSize = ({
  title,
  subtitle,
  icon,
  label,
}: {
  title: string;
  subtitle?: string;
  icon: SvgIcon;
  label?: string;
}): JSX.Element => (
  <Box width="50%" pt="3x">
    <IconBlock title={title} size="200" icon={icon} ariaLabel={label}>
      {subtitle && (
        <Text size="100" textColor="tertiary">
          {subtitle}
        </Text>
      )}
    </IconBlock>
  </Box>
);

const PetSizes = ({
  petsAttributes,
  dogSizes = DEFAULT_DOG_SIZES,
  weightLabel = DEFAULT_WEIGHT_LABEL,
  ...sizesAndTypes
}: Props): JSX.Element => {
  const { t } = useLingui();
  const WeightLabelConstants = {
    kg: t`kilograms`,
    lbs: t`pounds`,
  };
  const allowedPetsAttributes = petsAttributes.filter(
    (petAttribute) => sizesAndTypes[petAttribute.key]
  );

  // Make a pet size range more readable by turning "10-40" to "10 to 40",
  // or "101+" to "over 101"
  const getLabel = (range?: string): string | undefined => {
    // We don't use label prop for IconBlock on native.
    if (!range || process.env.JS_ENV_NATIVE) return undefined;

    if (range.indexOf('-') !== -1) {
      const [low, high] = range.split('-');
      return t({
        message: `${low} to ${high} ${weightLabel}`,
        comment: 'Used to format pet weights. For example: 10 to 40 lbs.',
      });
    }

    const num = range.split('+')[0];
    return t({
      message: `over ${num} ${weightLabel}`,
      comment: 'Used to format pet weights. For example: over 101 lbs.',
    });
  };

  return (
    <Flex flexDirection="row" width="100%" flexWrap="wrap">
      {allowedPetsAttributes.map((attribute) => {
        const dogSize = dogSizes.find((d) => d.key === attribute.key);
        const title = isCat(attribute) ? attribute.value : dogSize?.range || '';
        const label = isCat(attribute) ? undefined : getLabel(dogSize?.range);

        return (
          <PetSize
            key={attribute.key}
            title={title}
            subtitle={isCat(attribute) ? null : WeightLabelConstants[weightLabel]}
            icon={icons[upperFirst(camelizeString(attribute.icon))]}
            label={label}
          />
        );
      })}
    </Flex>
  );
};

export default PetSizes;
